import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Chip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { getGalleryPhotos, getGalleryItemPhotos } from "api/gallery";
import { TEXT_SECONDARY } from "components/bento/bentoTokens";
import FilterBar from "./FilterBar";
import PhotoLightbox from "./PhotoLightbox";
import { DayHeader, PhotoTile, PhotoListRow, TileSkeleton, RowSkeleton, EmptyHint, groupByDay, fmtTime } from "./parts";

const GRID_SX = {
  display: "grid",
  gap: "12px",
  gridTemplateColumns: "repeat(auto-fill, minmax(158px, 1fr))",
};

const LIST_SX = { display: "flex", flexDirection: "column", gap: "8px" };

const DEFAULT_FILTERS = { q: "", customer: null, preset: "all", from: "", to: "", category: "" };

const SOURCES = ["shipping", "wallet", "purchase", "branchCash"];
// "All" merges every source into one feed client-side — there's no backend
// endpoint for it. Fetches this many rows per source instead of the usual
// paged 48; meant for finding one customer across sources, not deep browsing.
const ALL_SOURCES_LIMIT = 60;

// Payment evidence album: four sources behind one tab —
//   shipping   = ShippingPayment cash receipts / bank slips
//   wallet     = WalletTopupRequest top-up slips
//   purchase   = Transaction{purpose:"purchase"} evidence (purchase orders)
//   branchCash = Transaction{ledgerType:"branch_cash"} evidence (deposit-to-bank / adjustment)
const PaymentsTab = () => {
  const { t, i18n } = useTranslation();
  const [source, setSource] = useState("shipping"); // shipping | wallet | purchase | branchCash
  const [viewMode, setViewMode] = useState("tile");
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null); // { photos, index, itemPhotos }
  const debounceRef = useRef();

  const load = useCallback(
    async (pageNum, append) => {
      setLoading(true);
      const base = {
        from: filters.from,
        to: filters.to,
        q: filters.q,
        customerId: filters.customer?._id || "",
      };
      if (source === "all") {
        // No backend "all payments" endpoint — fan out to the 4 sources and
        // merge client-side, newest-first. No true cross-source pagination
        // (see ALL_SOURCES_LIMIT); this is a one-shot fetch, not appended.
        const results = await Promise.all(
          SOURCES.map((src) => getGalleryPhotos({ album: src, ...base, page: 1, limit: ALL_SOURCES_LIMIT }))
        );
        const failed = results.find((r) => !r.success);
        if (failed) {
          toast.error(failed.message);
        } else {
          const merged = results.flatMap((res, i) => res.data.map((row) => ({ ...row, _src: SOURCES[i] })));
          merged.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
          setRows(merged);
          setTotal(merged.length);
        }
        setPage(1);
      } else {
        const res = await getGalleryPhotos({ album: source, ...base, category: filters.category, page: pageNum, limit: 48 });
        if (res.success) {
          setRows((prev) => (append ? [...prev, ...res.data] : res.data));
          setTotal(res.total);
          setPage(pageNum);
        } else {
          toast.error(res.message);
        }
      }
      setLoading(false);
    },
    [filters, source]
  );

  // Resolves which source a row actually came from — the merged rows carry
  // it as `_src`; a single-source view already knows since it's the only one.
  const rowSource = (p) => (source === "all" ? p._src : source);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(1, false), 300);
    return () => clearTimeout(debounceRef.current);
  }, [load]);

  const ITEM_ID_KEY = { shipping: "paymentId", wallet: "requestId", purchase: "transactionId", branchCash: "transactionId" };

  const openLightbox = async (photo, index, dayPhotos) => {
    const psrc = rowSource(photo);
    const itemId = photo[ITEM_ID_KEY[psrc]];
    const res = await getGalleryItemPhotos(psrc, itemId);
    setLightbox({
      album: psrc,
      photos: dayPhotos,
      itemPhotos: res.success ? res.data.photos : null,
      index,
    });
  };

  const categories = {
    shipping: [
      { value: "cash", label: t("gallery.method.cash") },
      { value: "bank", label: t("gallery.method.bank") },
    ],
    wallet: [
      { value: "approved", label: t("gallery.status.approved") },
      { value: "pending", label: t("gallery.status.pending") },
      { value: "rejected", label: t("gallery.status.rejected") },
    ],
    purchase: [],
    branchCash: [
      { value: "withdraw", label: t("gallery.branchCashPurpose.withdraw") },
      { value: "adjustment", label: t("gallery.branchCashPurpose.adjustment") },
    ],
  }[source];

  const tileTitle = (p, psrc) => {
    if (psrc === "shipping") return p.receiptId;
    if (psrc === "branchCash") return `${p.amount ?? ""} ${p.currency ?? ""}`;
    return p.customerName || t(`gallery.source.${psrc}`);
  };
  const tileSubtitle = (p, psrc) => {
    if (psrc === "shipping") return `${p.customerName || ""} · ${fmtTime(p.timestamp)}`;
    if (psrc === "branchCash") return fmtTime(p.timestamp);
    return `${p.amount ?? ""} ${p.currency ?? ""} · ${fmtTime(p.timestamp)}`;
  };
  const tileBadge = (p, psrc) => {
    if (psrc === "shipping") return t(`gallery.method.${p.method === "cash" ? "cash" : "bank"}`);
    if (psrc === "wallet") return t(`gallery.status.${p.status}`, p.status);
    if (psrc === "branchCash") return t(`gallery.branchCashPurpose.${p.purpose}`, p.purpose);
    return null;
  };
  // List view has a dedicated footer line for timestamp/station/extra, so unlike
  // tileSubtitle it doesn't need to cram the time in — and title already carries
  // customerName for wallet/purchase, so only shipping needs it repeated here.
  const rowSubtitle = (p, psrc) => (psrc === "shipping" ? p.customerName || "" : null);
  const rowExtra = (p) => (p.amount != null ? `${p.amount} ${p.currency ?? ""}` : null);

  const sections = groupByDay(rows);
  // "All" is a one-shot merge (see load()), not a paged fetch — nothing to load more of.
  const canLoadMore = rows.length < total && source !== "all";

  return (
    <Box>
      {/* source switch: shipping receipts / top-up slips / purchase orders / branch cash */}
      <Box sx={{ display: "flex", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
        {["all", ...SOURCES].map((s) => (
          <Chip
            key={s}
            label={t(`gallery.source.${s}`)}
            color="secondary"
            variant={source === s ? "filled" : "outlined"}
            onClick={() => {
              if (s === source) return;
              setSource(s);
              // Clear immediately — stale rows from the other source would render
              // against the wrong image endpoint (404s) until the reload lands.
              setRows([]);
              setTotal(0);
              setFilters((f) => ({ ...f, category: "" }));
            }}
          />
        ))}
      </Box>

      <FilterBar
        filters={filters}
        onChange={setFilters}
        categories={categories}
        hideCustomerFilter={source === "branchCash"}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        searchPlaceholder={
          { shipping: t("gallery.searchReceipts"), wallet: t("gallery.searchTopups"), purchase: t("gallery.searchPurchases"), branchCash: t("gallery.searchBranchCash") }[
            source
          ]
        }
      />

      <Typography sx={{ fontSize: 12.5, color: TEXT_SECONDARY, mb: 0.5 }}>
        {t("gallery.resultPhotos", { count: total })}
      </Typography>

      {loading && rows.length === 0 && (
        <Box sx={viewMode === "list" ? LIST_SX : GRID_SX}>
          {Array.from({ length: 12 }).map((_, i) => (viewMode === "list" ? <RowSkeleton key={i} /> : <TileSkeleton key={i} />))}
        </Box>
      )}

      {!loading && rows.length === 0 && <EmptyHint title={t("gallery.emptyTitle")} hint={t("gallery.emptyHint")} />}

      {sections.map((sec) => (
        <Box key={sec.key}>
          <DayHeader
            date={sec.date}
            count={sec.items.length}
            todayLabel={t("gallery.today")}
            photosLabel={t("gallery.photos")}
            lang={i18n.language}
          />
          <Box sx={viewMode === "list" ? LIST_SX : GRID_SX}>
            {sec.items.map((p, i) => {
              const psrc = rowSource(p);
              return viewMode === "list" ? (
                <PhotoListRow
                  key={`${p.filename}-${i}`}
                  album={psrc}
                  photo={p}
                  title={tileTitle(p, psrc)}
                  subtitle={rowSubtitle(p, psrc)}
                  badge={tileBadge(p, psrc)}
                  stationBadge={p.stationName || null}
                  timestamp={p.timestamp}
                  extra={rowExtra(p)}
                  onClick={() => openLightbox(p, sec.items.indexOf(p), sec.items)}
                />
              ) : (
                <PhotoTile
                  key={`${p.filename}-${i}`}
                  album={psrc}
                  photo={p}
                  title={tileTitle(p, psrc)}
                  subtitle={tileSubtitle(p, psrc)}
                  badge={tileBadge(p, psrc)}
                  stationBadge={p.stationName || null}
                  onClick={() => openLightbox(p, sec.items.indexOf(p), sec.items)}
                />
              );
            })}
          </Box>
        </Box>
      ))}

      {canLoadMore && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Button variant="outlined" color="secondary" disabled={loading} onClick={() => load(page + 1, true)}>
            {loading ? t("common.loading") : t("gallery.loadMore")}
          </Button>
        </Box>
      )}

      {lightbox && (
        <PhotoLightbox
          open
          onClose={() => setLightbox(null)}
          album={lightbox.album}
          photos={lightbox.photos}
          index={lightbox.index}
          onIndexChange={(i) => setLightbox((lb) => ({ ...lb, index: i }))}
          itemPhotos={lightbox.itemPhotos}
          downloadBaseName={(p) => {
            if (lightbox.album === "shipping") return p.receiptId;
            if (lightbox.album === "wallet") return `topup_${p.requestId}`;
            if (lightbox.album === "purchase") return `purchase_${p.transactionId}`;
            return `branchcash_${p.transactionId}`;
          }}
          meta={(p) => {
            if (lightbox.album === "shipping") {
              return [
                { k: t("gallery.receiptId"), v: p.receiptId },
                { k: t("gallery.customer"), v: p.customerName },
                { k: t("gallery.method.label"), v: p.method ? t(`gallery.method.${p.method === "cash" ? "cash" : "bank"}`) : null },
                { k: t("gallery.amount"), v: p.amount != null ? `${p.amount} ${p.currency}` : null },
                { k: t("gallery.collectedBy"), v: p.collectedByName },
                { k: t("gallery.station"), v: p.stationName },
              ];
            }
            if (lightbox.album === "purchase") {
              return [
                { k: t("gallery.customer"), v: p.customerName },
                { k: t("gallery.amount"), v: p.amount != null ? `${p.amount} ${p.currency}` : null },
                { k: t("gallery.collectedBy"), v: p.collectedByName },
                { k: t("gallery.station"), v: p.stationName },
              ];
            }
            if (lightbox.album === "branchCash") {
              return [
                { k: t("gallery.branchCashPurpose.label"), v: p.purpose ? t(`gallery.branchCashPurpose.${p.purpose}`, p.purpose) : null },
                { k: t("gallery.amount"), v: p.amount != null ? `${p.amount} ${p.currency}` : null },
                { k: t("gallery.collectedBy"), v: p.collectedByName },
                { k: t("gallery.station"), v: p.stationName },
              ];
            }
            return [
              { k: t("gallery.customer"), v: p.customerName },
              { k: t("gallery.amount"), v: p.amount != null ? `${p.amount} ${p.currency}` : null },
              { k: t("gallery.station"), v: p.stationName },
            ];
          }}
        />
      )}
    </Box>
  );
};

export default PaymentsTab;
